import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { Gem, FlaskConical, Eye, BadgeCheck, Sparkles, Leaf, Sprout, HeartHandshake } from "lucide-react";
import { brand, home, product, whyChoose, featuredProduct, whyFormula, aboutHome, nourish, moreThan } from "../content/site";
import { MaskLine, Reveal, Eyebrow } from "../components/Reveal";
import { BottleVisual } from "../components/BottleVisual";
import { Marquee } from "../components/Marquee";

const whyChooseIcons = [Gem, FlaskConical, Eye, BadgeCheck, Sparkles];

const trustBar = [
  [Gem, "Premium Quality Ingredients"],
  [FlaskConical, "100% Magnesium Bisglycinate Chelate"],
  [Leaf, "Vegan Friendly"],
  [Sprout, "Non-GMO"],
  [HeartHandshake, "Proudly Supporting Nourish Fund"],
];

export default function Home() {
  const heroRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(my, { stiffness: 55, damping: 16 });
  const rotateY = useSpring(mx, { stiffness: 55, damping: 16 });
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bottleY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const onMouseMove = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 9);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} onMouseMove={onMouseMove} className="relative overflow-hidden" data-testid="home-hero">
        <div className="pointer-events-none absolute -right-40 top-16 h-[540px] w-[540px] rounded-full bg-[#3B604B]/15 blur-3xl" />
        <div className="mx-auto grid max-w-7xl gap-14 px-4 pb-20 pt-16 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-16 lg:pb-28 lg:pt-24">
          <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-7">
            <Reveal>
              <Eyebrow>{home.heroEyebrow}</Eyebrow>
            </Reveal>
            <h1
              data-testid="hero-title"
              className="mt-7 font-serif text-6xl font-light leading-[0.98] tracking-tight text-stone-900 sm:text-7xl lg:text-[6.2rem]"
            >
              {home.heroLines.map((line, i) => (
                <MaskLine key={line} delay={0.15 + i * 0.18}>
                  {i === 1 ? <span className="italic text-emerald-900">{line}</span> : line}
                </MaskLine>
              ))}
            </h1>
            <Reveal delay={0.55}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">{home.heroBody}</p>
            </Reveal>
            <Reveal delay={0.7}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/shop"
                  data-testid="hero-shop-now-button"
                  className="bg-stone-900 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-50 transition-colors duration-300 hover:bg-emerald-900"
                >
                  Shop Magnesium Glycinate
                </Link>
                <Link
                  to="/why-el-well"
                  data-testid="hero-why-button"
                  className="border border-stone-900 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50"
                >
                  Why EL Well
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.85}>
              <div className="mt-16 flex gap-12 border-t border-stone-900/10 pt-7">
                {[
                  [`${product.capsules}`, "Capsules per bottle"],
                  ["1", "Active ingredient"],
                  ["UK", "Brand"],
                ].map(([v, l]) => (
                  <div key={l}>
                    <p className="font-serif text-3xl font-light text-stone-900">{v}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{l}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </motion.div>

          <div className="relative flex items-center justify-center lg:col-span-5">
            <motion.div
              style={{ y: bottleY, rotateX, rotateY, transformPerspective: 1100 }}
              className="relative will-change-transform"
            >
              <div className="elwell-float">
                <div className="overflow-hidden border border-stone-900/10 bg-[#F3EFE6] p-6 sm:p-8">
                  <BottleVisual className="relative w-52 sm:w-64 lg:w-72" />
                </div>
              </div>
              <div className="absolute -left-6 top-6 hidden border border-stone-900/15 bg-[#FBF9F5] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-700 sm:block">
                {product.edition} — First Formula
              </div>
              <div className="absolute -right-4 bottom-10 border border-stone-900/15 bg-[#FBF9F5] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-700">
                {product.capsules} Capsules
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-stone-900/10" data-testid="trust-bar">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-stone-900/10 sm:grid-cols-3 lg:grid-cols-5">
          {trustBar.map(([Icon, label], i) => (
            <div
              key={label}
              className={`flex flex-col items-start gap-4 bg-[#FBF9F5] p-6 lg:p-7 ${
                i === 4 ? "col-span-2 sm:col-span-1" : ""
              }`}
            >
              <Icon className="h-5 w-5 text-emerald-900" strokeWidth={1.25} />
              <p className="font-mono text-[10px] font-medium uppercase leading-relaxed tracking-[0.18em] text-stone-700">
                {label}
              </p>
            </div>
          ))}
        </div>
      </section>

      <Marquee />

      {/* WHY CHOOSE EL WELL */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32" data-testid="why-choose-section">
        <Reveal>
          <Eyebrow>{whyChoose.title}</Eyebrow>
          <p className="mt-7 max-w-3xl text-base leading-relaxed text-stone-600 sm:text-lg">{whyChoose.intro}</p>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-stone-900/10 bg-stone-900/10 sm:grid-cols-2 lg:grid-cols-3">
          {whyChoose.features.map((f, i) => {
            const Icon = whyChooseIcons[i];
            return (
              <Reveal key={f} delay={i * 0.06} className={`bg-[#FBF9F5] ${i === 4 ? "sm:col-span-2" : ""}`}>
                <div className="flex h-full flex-col p-9 lg:p-11">
                  <Icon className="h-6 w-6 text-emerald-900" strokeWidth={1.25} />
                  <p className="mt-6 font-serif text-2xl font-light leading-snug text-stone-900">{f}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ABOUT EL WELL */}
      <section className="border-t border-stone-900/10" data-testid="home-about-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-16 lg:py-32">
          <div className="lg:col-span-4">
            <Reveal>
              <Eyebrow>{aboutHome.eyebrow}</Eyebrow>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            {aboutHome.paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p
                  className={`max-w-2xl leading-relaxed ${
                    i === 0
                      ? "font-serif text-2xl font-light leading-snug text-stone-900 sm:text-3xl"
                      : "mt-7 text-base text-stone-600"
                  }`}
                >
                  {p}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link
                to="/about"
                data-testid="home-about-link"
                className="mt-10 inline-block border border-stone-900 px-8 py-3.5 font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50"
              >
                About EL Well
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MORE THAN A WELLNESS BRAND */}
      <section className="border-t border-stone-900/10 bg-[#F3EFE6]" data-testid="more-than-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-24 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-16 lg:py-32">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Our beginnings</Eyebrow>
              <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-stone-900 sm:text-5xl">
                <MaskLine inView>More Than a</MaskLine>
                <MaskLine inView delay={0.12}>
                  <span className="italic text-emerald-900">Wellness Brand</span>
                </MaskLine>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            {moreThan.paragraphs.map((p, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <p className={`max-w-2xl text-base leading-relaxed text-stone-600 ${i > 0 ? "mt-6" : ""}`}>{p}</p>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link
                to="/why-el-well"
                data-testid="read-our-story-button"
                className="mt-10 inline-block bg-stone-900 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-50 transition-colors duration-300 hover:bg-emerald-900"
              >
                {moreThan.cta}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EDITORIAL STATEMENT */}
      <section className="border-b border-stone-900/10">
        <div className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
          <Eyebrow>Our editorial standard</Eyebrow>
          <h2 className="mt-8 font-serif text-5xl font-light leading-[1.05] text-stone-900 sm:text-6xl lg:text-7xl">
            <MaskLine inView>Fewer ingredients.</MaskLine>
            <MaskLine inView delay={0.14}>
              <span className="italic text-emerald-900">Better chosen.</span>
            </MaskLine>
          </h2>
        </div>
      </section>

      {/* EDITORIAL IMAGE */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
        <Reveal>
          <div className="overflow-hidden border border-stone-900/10">
            <img
              src="/assets/bottle-wide.png"
              alt="EL Well Magnesium Glycinate — premium black bottle with capsules and botanical leaves"
              className="w-full object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
      </section>

      {/* WHY MAGNESIUM */}
      <section className="border-t border-stone-900/10" data-testid="why-magnesium-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 py-24 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-16 lg:py-32">
          <div className="lg:col-span-5">
            <Reveal>
              <Eyebrow>Understanding magnesium</Eyebrow>
            </Reveal>
            <h2 className="mt-6 font-serif text-5xl font-light leading-[1.05] tracking-tight text-stone-900 sm:text-6xl">
              <MaskLine inView>Why</MaskLine>
              <MaskLine inView delay={0.14}>
                <span className="italic text-emerald-900">Magnesium?</span>
              </MaskLine>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <p className="max-w-xl text-base leading-relaxed text-stone-600">
                Magnesium contributes to normal muscle function, normal functioning of the nervous system and normal
                psychological function. It also contributes to normal energy-yielding metabolism and the reduction of
                tiredness and fatigue.
              </p>
            </Reveal>
            <div className="mt-10">
              {[
                "Magnesium contributes to normal muscle function.",
                "Magnesium contributes to normal functioning of the nervous system.",
                "Magnesium contributes to normal psychological function.",
                "Magnesium contributes to normal energy-yielding metabolism.",
                "Magnesium contributes to the reduction of tiredness and fatigue.",
              ].map((claim, i) => (
                <Reveal key={claim} delay={0.1 + i * 0.05}>
                  <div className="flex items-baseline gap-6 border-t border-stone-900/15 py-5">
                    <p className="font-mono text-[10px] tracking-[0.2em] text-emerald-900/70">0{i + 1}</p>
                    <p className="font-serif text-xl font-light text-stone-900 sm:text-2xl">{claim}</p>
                  </div>
                </Reveal>
              ))}
              <div className="border-t border-stone-900/15" />
            </div>
            <Reveal delay={0.35}>
              <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                Authorised health claims for magnesium in Great Britain
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCT */}
      <section className="bg-[#1E3A2D] text-stone-100" data-testid="home-product-spotlight">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-24 sm:px-8 lg:grid-cols-2 lg:px-16 lg:py-32">
          <Reveal className="order-2 lg:order-1">
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-emerald-100/70">
              {product.edition} — First Formula
            </p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-tight text-stone-50 sm:text-5xl">
              {featuredProduct.title}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-stone-300">{featuredProduct.body}</p>
            <ul className="mt-8 space-y-3">
              {product.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-sm text-stone-200">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-emerald-200/80" />
                  {h}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex items-baseline gap-4">
              <p data-testid="spotlight-price" className="font-serif text-4xl font-light text-stone-50">
                £{product.price.toFixed(2)}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">
                {product.capsules} capsules
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <button
                type="button"
                data-testid="spotlight-add-to-bag"
                disabled
                className="cursor-not-allowed bg-stone-50/40 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900/50"
              >
                Coming Soon
              </button>
              <Link
                to={`/product/${product.slug}`}
                data-testid="spotlight-view-product"
                className="border border-stone-100/40 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-100 transition-colors duration-300 hover:bg-stone-50 hover:text-stone-900"
              >
                Learn More
              </Link>
            </div>
          </Reveal>
          <Reveal className="order-1 lg:order-2">
            <div className="relative mx-auto max-w-md overflow-hidden border border-stone-100/15">
              <BottleVisual className="relative w-full" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY OUR FORMULA */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32" data-testid="why-formula-section">
        <Reveal>
          <Eyebrow>{whyFormula.title}</Eyebrow>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-stone-900/10 bg-stone-900/10 sm:grid-cols-2 lg:grid-cols-4">
          {whyFormula.cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06} className="bg-[#FBF9F5]">
              <div className="flex h-full flex-col p-9 lg:p-10">
                <p className="font-serif text-2xl font-light text-stone-300">0{i + 1}</p>
                <p className="mt-5 font-serif text-xl font-light leading-snug text-stone-900 sm:text-2xl">{c.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NOURISH FUND */}
      <section className="border-y border-stone-900/10 bg-[#F3EFE6]" data-testid="nourish-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-16 lg:py-32">
          <Reveal className="lg:col-span-4">
            <div
              data-testid="nourish-logo-mark"
              className="inline-flex flex-col items-center gap-4 border border-stone-900/15 bg-[#FBF9F5] px-10 py-9"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-900">
                <Leaf className="h-6 w-6 text-stone-50" strokeWidth={1.25} />
              </span>
              <span className="text-center font-mono text-[10px] font-medium uppercase leading-relaxed tracking-[0.3em] text-stone-900">
                The Nourish
                <br />
                Fund
              </span>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal>
              <Eyebrow>{nourish.title}</Eyebrow>
              <p className="mt-7 max-w-2xl font-serif text-2xl font-light leading-snug text-stone-900 sm:text-3xl">
                {nourish.lead}
              </p>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-600">{nourish.body}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
        <div className="items-end justify-between gap-10 lg:flex">
          <div>
            <Reveal>
              <Eyebrow>Talk to us</Eyebrow>
            </Reveal>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-stone-900 sm:text-5xl">
              <MaskLine inView>Every question answered,</MaskLine>
              <MaskLine inView delay={0.12}>
                <span className="italic text-emerald-900">personally.</span>
              </MaskLine>
            </h2>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-stone-600">
                Considering EL Well, or already a customer? We read and reply to every message ourselves.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <Link
              to="/contact"
              data-testid="home-contact-cta"
              className="mt-10 inline-block border border-stone-900 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-900 transition-colors duration-300 hover:bg-stone-900 hover:text-stone-50 lg:mt-0"
            >
              Contact Us
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
